/**
 * 富文本邮件编辑器组件
 *
 * 基于 TipTap（ProseMirror）构建的富文本编辑器，提供完整的邮件编辑功能。
 *
 * 自定义元素：`crm-rich-editor`
 *
 * 核心能力：
 * - **文本格式**：加粗、斜体、删除线、下划线、标题（H2/H3）
 * - **颜色/高亮**：文字颜色、背景高亮色（调色板选择）
 * - **字号**：12px ~ 48px 可选
 * - **列表**：有序列表、无序列表
 * - **对齐**：左对齐、居中、右对齐
 * - **缩进**：增加/减少缩进（Tab / Shift-Tab 快捷键）
 * - **链接**：插入/编辑/移除超链接
 * - **图片**：插入本地图片（上传为内联图片）或 URL 图片
 * - **表格**：插入 N×M 表格
 * - **附件**：上传文件附件（派发事件给父组件处理）
 * - **拖放上传**：支持拖拽文件到编辑器上传
 * - **签名块**：专用的 signatureBlock 节点类型，便于可靠查找和移除
 * - **片段插入**：通过 insertAtCursor 在光标位置插入任意 HTML
 * - **工具栏扩展**：通过 slot="toolbar-end" 和 slot="toolbar-extra" 支持外部注入按钮
 *
 * 架构设计：
 * - **DivParagraph 扩展**：将 `<div>` 标签作为段落容器（而非默认的 `<p>`），
 *   以兼容邮件客户端对 HTML 结构的期望
 * - **SignatureBlock 节点**：自定义 ProseMirror 节点类型，通过 data-signature-id
 *   属性标识签名块，实现可靠的查找/移除（不依赖脆弱的 HTML 字符串匹配）
 * - **Indent 扩展**：通过全局属性为 div 和 heading 节点添加 indent 属性，
 *   使用 paddingLeft 实现，Tab/Shift-Tab 快捷键切换
 *
 * 依赖模块：
 * - TipTap 核心及多个扩展（StarterKit, Paragraph, Link, Placeholder, Color,
 *   TextStyle, Highlight, FontSize, TextAlign, Image, Table 等）
 * - `uploadAttachment` / `uploadInlineImage`：附件和图片上传 API
 * - `toast`：错误提示
 */
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Editor, mergeAttributes, Extension, Node } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Color } from '@tiptap/extension-color';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { Image } from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { BaseElement } from '../base-element.js';
import { uploadAttachment, uploadInlineImage, API_BASE_URL } from '../api.js';
import { toast } from './toast.js';

/**
 * DivParagraph - 自定义段落扩展
 *
 * 将 TipTap 默认的 `<p>` 标签替换为 `<div>` 标签作为段落容器。
 * 这是为了兼容邮件客户端（如 Outlook、Gmail）对 HTML 结构的处理习惯，
 * 它们通常更好地处理 `<div>` 而非 `<p>`。
 */
const DivParagraph = Paragraph.extend({
  // 解析 HTML 时将 <div> 和 <p> 标签都识别为段落（引用块常用 <p>，避免丢失段落结构）
  parseHTML() {
    return [{ tag: 'div' }, { tag: 'p' }];
  },
  // 渲染时输出为 <div> 标签
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
});

/**
 * SignatureBlock - 签名块自定义节点
 *
 * 专用于邮件签名的 ProseMirror 块级节点。通过 data-signature-id 属性标识，
 * 使签名块可以通过节点类型名称（'signatureBlock'）可靠地查找和移除，
 * 而不依赖脆弱的 HTML 字符串匹配。
 *
 * 解析规则：匹配 `div[data-signature-id]` 标签，优先级 60（高于普通 div）
 * 渲染规则：输出 `<div data-signature-id="...">` 标签
 */
// Dedicated node for signature blocks — reliable find/remove via type name
const SignatureBlock = Node.create({
  name: 'signatureBlock',
  group: 'block',
  content: 'block+',
  parseHTML() {
    return [{ tag: 'div[data-signature-id]', priority: 60 }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-signature-id': HTMLAttributes['data-sig-id'] }), 0];
  },
  addAttributes() {
    return {
      'data-sig-id': { default: null },
    };
  },
});

/** 缩进步长（像素）：每次缩进增加 24px 的 padding-left */
const INDENT_STEP = 24;

/**
 * Indent 扩展的类型声明（为 TipTap Commands 接口添加 indent/outdent 命令）
 */
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: { indent: () => ReturnType };
    outdent: { outdent: () => ReturnType };
  }
}

/**
 * Indent - 缩进扩展
 *
 * 为 div 和 heading 节点添加全局 indent 属性。
 * - parseHTML：从 HTML 元素的 paddingLeft 解析缩进级别
 * - renderHTML：将缩进级别渲染为 style="padding-left: Npx"
 * - indent()：选中范围内所有 div/heading 的 indent +1
 * - outdent()：选中范围内所有 div/heading 的 indent -1（最小 0）
 * - 快捷键：Tab = indent, Shift-Tab = outdent
 */
const Indent = Extension.create({
  name: 'indent',
  addGlobalAttributes() {
    return [{
      types: ['div', 'heading'],
      attributes: {
        indent: {
          default: 0,
          parseHTML: el => parseInt((el as HTMLElement).style.paddingLeft || '0', 10) / INDENT_STEP || 0,
          renderHTML: attrs => attrs.indent ? { style: `padding-left: ${attrs.indent * INDENT_STEP}px` } : {},
        },
      },
    }];
  },
  addCommands() {
    return {
      indent: () => ({ tr, state, dispatch }: { tr: any; state: any; dispatch: any }) => {
        const { selection } = state;
        tr = tr.setSelection(selection);
        const { from, to } = selection;
        state.doc.nodesBetween(from, to, (node: any, pos: any) => {
          if (node.type.name === 'div' || node.type.name.startsWith('heading')) {
            const indent = (node.attrs.indent || 0) + 1;
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent });
          }
        });
        if (dispatch) dispatch(tr);
        return true;
      },
      outdent: () => ({ tr, state, dispatch }: { tr: any; state: any; dispatch: any }) => {
        const { selection } = state;
        tr = tr.setSelection(selection);
        const { from, to } = selection;
        state.doc.nodesBetween(from, to, (node: any, pos: any) => {
          if (node.type.name === 'div' || node.type.name.startsWith('heading')) {
            const indent = Math.max(0, (node.attrs.indent || 0) - 1);
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent });
          }
        });
        if (dispatch) dispatch(tr);
        return true;
      },
    };
  },
  addKeyboardShortcuts() {
    return { Tab: () => this.editor.commands.indent(), 'Shift-Tab': () => this.editor.commands.outdent() };
  },
});

@customElement('crm-rich-editor')
export class CrmRichEditor extends BaseElement {
  /** 表单字段名（用于隐藏 input 的 name 属性） */
  @property({ type: String })
  name = 'content';

  /** 编辑器 HTML 内容（双向绑定：外部设置或编辑器内部更新） */
  @property({ type: String })
  value = '';

  /** 占位符文本（编辑器为空时显示） */
  @property({ type: String })
  placeholder = '输入邮件内容...';

  /** 是否必填（控制隐藏 input 的 required 属性） */
  @property({ type: Boolean })
  required = false;

  /** 禁用滚动（转发邮件场景下编辑器高度自适应，不出现滚动条） */
  @property({ type: Boolean })
  noScroll = false;

  /**
   * 自定义图片上传器
   * 如果提供，图片上传使用此函数而非默认的 uploadInlineImage API。
   * 返回 { cid, displayUrl } 用于在编辑器中显示图片。
   */
  @property({ attribute: false })
  customImageUploader?: (file: File) => Promise<{ cid: string; displayUrl: string }>;

  // ---- 工具栏状态 ----
  // 这些状态通过 syncToolbarState() 在编辑器选区变化时同步，
  // 用于控制工具栏按钮的 active 高亮状态。

  /** TipTap 编辑器实例 */
  @state()
  private editor: Editor | null = null;

  /** 当前选区是否在加粗文本中 */
  @state()
  private isBold = false;
  /** 当前选区是否在斜体文本中 */
  @state()
  private isItalic = false;
  /** 当前选区是否在删除线文本中 */
  @state()
  private isStrike = false;
  /** 当前选区是否在下划线文本中 */
  @state()
  private isUnderline = false;
  /** 当前选区是否为 H2 标题 */
  @state()
  private isH2 = false;
  /** 当前选区是否在无序列表中 */
  @state()
  private isBulletList = false;
  /** 当前选区是否在有序列表中 */
  @state()
  private isOrderedList = false;
  /** 当前选区是否在引用块中 */
  @state()
  private isBlockquote = false;
  /** 当前选区是否在链接中 */
  @state()
  private isLink = false;

  // ---- 颜色/高亮/字号状态 ----

  /** 当前文字颜色（默认深色 #1c2024） */
  @state()
  private textColor = '#1c2024';
  /** 当前高亮颜色（空字符串表示无高亮） */
  @state()
  private highlightColor = '';
  /** 是否显示文字颜色选择面板 */
  @state()
  private showColorPicker = false;
  /** 是否显示高亮颜色选择面板 */
  @state()
  private showHighlightPicker = false;

  /** 当前字号（如 '14px'） */
  @state()
  private fontSize = '14px';
  /** 是否显示字号选择面板 */
  @state()
  private showFontSizePicker = false;

  /** 当前文本对齐方式 */
  @state()
  private textAlign: 'left' | 'center' | 'right' = 'left';

  // ---- 模态框状态 ----

  /** 是否显示链接编辑模态框 */
  @state()
  private showLinkModal = false;
  /** 链接 URL 输入值 */
  @state()
  private linkUrl = '';

  /** 是否显示图片插入模态框 */
  @state()
  private showImageModal = false;
  /** 图片 URL 输入值 */
  @state()
  private imageUrl = '';

  /** 是否显示表格插入模态框 */
  @state()
  private showTableModal = false;
  /** 表格行数（默认 3） */
  @state()
  private tableRows = 3;
  /** 表格列数（默认 3） */
  @state()
  private tableCols = 3;
  /**
   * 遮罩点击追踪标志
   * 用于区分点击的是遮罩背景还是模态框内容：
   * mousedown 记录是否点在遮罩上，click 时据此判断是否关闭。
   */
  private _backdropMousedown = false;

  /** 是否处于拖拽文件悬停状态（显示拖放提示遮罩） */
  @state()
  private isDragOver = false;

  // ---- 非响应式私有字段 ----

  /** 文件附件选择 input（动态创建，用于附件上传） */
  private fileInput: HTMLInputElement | null = null;
  /** 已上传的附件 ID 列表（供外部通过 exportAttachmentIds 获取） */
  private uploadedAttachmentIds: number[] = [];
  /** 已上传的内联图片 ID 和 CID 列表 */
  private uploadedInlineImageIds: { id: number; cid: string }[] = [];
  /** 图片文件选择 input（动态创建，用于图片上传） */
  private imageFileInput: HTMLInputElement | null = null;
  /**
   * 最近一次派发 input 事件时的值
   * 用于避免外部设置 value 时触发不必要的内容重置（防止光标跳动）
   */
  private lastEmittedValue: string | null = null;

  /**
   * 组件样式
   *
   * 结构：
   * - .toolbar-wrapper：工具栏容器（sticky 定位，始终可见）
   * - .toolbar / .toolbar-extra-row：主工具栏行 + 扩展工具栏行
   * - .toolbar-btn：工具栏按钮（hover/active 状态）
   * - .toolbar-sep：工具栏分隔线
   * - .editor-wrap：编辑器外层容器（拖放高亮状态）
   * - .drag-overlay：拖放文件时的提示遮罩
   * - .editor-body：编辑器主体（ProseMirror 容器，内容样式）
   * - .hidden-input：隐藏的 input（用于表单提交）
   */
  static styles = css`
    :host { display: flex; flex-direction: column; min-height: 0; }

    .toolbar-wrapper {
      flex-shrink: 0;
      position: sticky;
      top: 0;
      z-index: 10;
      background: var(--color-surface, #ffffff);
    }
    .toolbar {
      display: flex;
      gap: 2px;
      padding: 8px 12px;
      border-bottom: 1px solid var(--color-border, #e0e1e6);
      flex-wrap: wrap;
    }
    .toolbar-extra-row {
      display: flex;
      gap: 4px;
      padding: 4px 12px;
      border-bottom: 1px solid var(--color-border, #e0e1e6);
      flex-wrap: wrap;
    }
    .toolbar-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 6px 8px;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      background: transparent;
      color: var(--color-text-secondary, #555860);
      transition: background 0.15s, color 0.15s;
    }
    .toolbar-btn:hover {
      background: var(--color-bg, #f0f0f3);
      color: var(--color-text, #1c2024);
    }
    .toolbar-btn.is-active {
      background: var(--color-bg, #f0f0f3);
      color: var(--color-text, #1c2024);
    }
    .toolbar-sep {
      width: 1px;
      height: 20px;
      background: var(--color-border, #e0e1e6);
      margin: 0 4px;
      align-self: center;
    }

    .editor-wrap {
      flex: 1;
      min-height: 0;
      border: none;
      display: flex;
      flex-direction: column;
      position: relative;
    }
    .editor-wrap:focus-within {
      box-shadow: none;
    }
    .editor-wrap.drag-over {
      border-color: #0d74ce;
      background: rgba(13, 116, 206, 0.06);
      box-shadow: 0 0 0 3px rgba(13, 116, 206, 0.15);
    }

    .drag-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: rgba(13, 116, 206, 0.04);
      pointer-events: none;
      z-index: 10;
    }
    .drag-overlay-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(13, 116, 206, 0.12);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .drag-overlay-text {
      font-size: 14px;
      font-weight: 500;
      color: #0d74ce;
    }

    .editor-body {
      flex: 1;
      min-height: 0;
      padding: 16px;
      overflow-y: auto;
      outline: none;
      font-size: 14px;
      line-height: 1.6;
      color: var(--color-text, #1c2024);
      font-family: var(--font-sans, system-ui, sans-serif);
    }
    .editor-body ul, .editor-body ol { margin: 0 0 8px; padding-left: 20px; }
    .editor-body h2 { margin: 0 0 8px; font-size: 1.25em; font-weight: 600; }
    .editor-body h3 { margin: 0 0 8px; font-size: 1.1em; font-weight: 600; }
    .editor-body a { color: var(--color-link, #0d74ce); text-decoration: underline; }
    .editor-body blockquote {
      border-left: 3px solid var(--color-border, #e0e1e6);
      margin: 0 0 8px;
      padding-left: 12px;
      color: var(--color-text-muted, #b0b4ba);
    }
    .editor-body hr { border: none; border-top: 1px solid var(--color-border, #e0e1e6); margin: 12px 0; }
    .editor-body img { max-width: 100%; height: auto; border-radius: 6px; margin: 4px 0; }
    .editor-body table { border-collapse: collapse; margin: 8px 0; }
    .editor-body tr { min-height: 0; }
    .editor-body td, .editor-body th { border: 1px solid var(--color-border, #e0e1e6); padding: 4px 8px; min-width: 60px; text-align: left; line-height: 1.3; }
    .editor-body th { background: var(--color-bg, #f0f0f3); font-weight: 600; }
    .editor-body .selectedCell { background: rgba(13,116,206,0.12); outline: 2px solid #0d74ce; }
    .editor-body pre {
      background: var(--color-bg, #f0f0f3);
      border-radius: 6px;
      padding: 12px;
      margin: 0 0 8px;
      font-family: var(--font-mono, monospace);
      font-size: 13px;
      overflow-x: auto;
    }
    .editor-body code {
      background: var(--color-bg, #f0f0f3);
      border-radius: 4px;
      padding: 2px 4px;
      font-family: var(--font-mono, monospace);
      font-size: 13px;
    }
    .editor-body pre code { background: none; padding: 0; }

    .editor-body .ProseMirror { outline: none; }
    .editor-body .ProseMirror-gapcursor { display: none; }
    .editor-body .ProseMirror-gapcursor:after { display: none; }

    .editor-body.no-scroll { overflow-y: visible; }

    .editor-body .is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: var(--color-text-muted, #b0b4ba);
      pointer-events: none;
      height: 0;
    }

    .hidden-input {
      position: absolute;
      width: 0; height: 0;
      opacity: 0; overflow: hidden;
    }
  `;

  /**
   * 组件挂载时触发，注册 ESC 键监听器
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleEscKey);
  }

  /**
   * 组件卸载时触发，移除事件监听器并销毁编辑器实例
   *
   * 销毁编辑器是防止内存泄漏的关键——ProseMirror 编辑器持有大量
   * DOM 引用和事件监听器，不销毁会导致严重的内存泄漏。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleEscKey);
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  }

  /**
   * ESC 键处理器：按顺序关闭打开的模态框
   *
   * 优先级：链接 > 图片 > 表格
   * 使用箭头函数绑定 this。
   */
  private handleEscKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (this.showLinkModal) this.closeLinkModal();
      else if (this.showImageModal) this.closeImageModal();
      else if (this.showTableModal) this.closeTableModal();
    }
  };

  /**
   * Lit firstUpdated 生命周期钩子：首次渲染完成后初始化 TipTap 编辑器
   *
   * 创建 Editor 实例并挂载到 .editor-body 元素上，配置所有扩展：
   * - StarterKit（段落关闭，使用自定义 DivParagraph）
   * - DivParagraph + SignatureBlock（自定义节点）
   * - TextStyle + Color + Highlight + FontSize（文本样式）
   * - TextAlign + Indent（段落格式）
   * - Link + Image + Table 系列（插入元素）
   * - Placeholder（空内容占位）
   *
   * onUpdate 回调：内容变化时更新 value 并派发 input 事件
   * onSelectionUpdate 回调：选区变化时同步工具栏状态
   */
  firstUpdated() {
    const editorEl = this.shadowRoot!.querySelector('.editor-body') as HTMLElement;
    if (!editorEl) return;

    this.editor = new Editor({
      element: editorEl,
      extensions: [
        StarterKit.configure({ heading: { levels: [2, 3] }, paragraph: false, link: false }),
        DivParagraph,
        SignatureBlock,
        TextStyle,
        Color.configure({ types: ['textStyle'] }),
        Highlight.configure({ multicolor: true }),
        FontSize,
        TextAlign.configure({ types: ['div', 'heading'] }),
        Indent,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        }),
        Image.configure({ inline: true, allowBase64: true }),
        Table.configure({ resizable: true }),
        TableRow,
        TableCell,
        TableHeader,
        Placeholder.configure({ placeholder: this.placeholder }),
      ],
      content: this.value || '',
      // 内容更新回调：同步 value 并派发 input 事件
      onUpdate: ({ editor }) => {
        this.value = editor.getHTML();
        this.lastEmittedValue = this.value;
        this.dispatchEvent(new CustomEvent('input', {
          detail: { value: this.value },
          bubbles: true,
          composed: true,
        }));
        this.syncToolbarState();
      },
      // 选区更新回调：同步工具栏按钮高亮状态
      onSelectionUpdate: () => {
        this.syncToolbarState();
      },
    });

    this.syncToolbarState();
  }

  /**
   * Lit updated 生命周期钩子：value 属性变化时同步到编辑器
   *
   * 防止循环更新：如果新值等于最近一次派发的值，说明是编辑器自身触发的更新，
   * 跳过同步以避免光标跳动。
   *
   * @param changed - 变化的属性 Map
   */
  updated(changed: Map<string, any>) {
    if (changed.has('value') && this.editor) {
      const newVal = this.value || '';
      if (newVal === this.lastEmittedValue) return;
      if (this.editor.getHTML() !== newVal) {
        this.editor.commands.setContent(newVal);
        this.lastEmittedValue = this.value = this.editor.getHTML();
      }
    }
  }

  /**
   * 同步工具栏状态到当前选区
   *
   * 查询编辑器当前选区的格式状态（加粗、斜体、列表等），
   * 更新对应的 @state 变量以驱动工具栏按钮的高亮。
   */
  private syncToolbarState() {
    if (!this.editor) return;
    this.isBold = this.editor.isActive('bold');
    this.isItalic = this.editor.isActive('italic');
    this.isStrike = this.editor.isActive('strike');
    this.isUnderline = this.editor.isActive('underline');
    this.isH2 = this.editor.isActive('heading', { level: 2 });
    this.isBulletList = this.editor.isActive('bulletList');
    this.isOrderedList = this.editor.isActive('orderedList');
    this.isBlockquote = this.editor.isActive('blockquote');
    this.isLink = this.editor.isActive('link');
    this.textAlign = (this.editor.isActive({ textAlign: 'center' }) ? 'center'
      : this.editor.isActive({ textAlign: 'right' }) ? 'right' : 'left');
  }

  /**
   * 打开链接编辑模态框
   *
   * 如果当前选区已在链接中，预填充现有 URL。
   */
  private setLink = () => {
    if (!this.editor) return;
    this.linkUrl = this.editor.getAttributes('link').href || '';
    this.showLinkModal = true;
  }

  /**
   * 确认链接设置：设置或移除链接
   *
   * URL 为空时移除链接，否则设置新链接。
   * 使用 extendMarkRange 确保覆盖整个链接标记范围。
   *
   * @param e - 表单提交事件
   */
  private confirmLink(e: Event) {
    e.preventDefault();
    if (!this.editor) return;
    const url = this.linkUrl.trim();
    if (url === '') {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    this.showLinkModal = false;
  }

  /** 关闭链接模态框 */
  private closeLinkModal = () => { this.showLinkModal = false; }

  /**
   * 设置文字颜色
   *
   * @param color - 颜色值（如 '#1c2024'）
   */
  private pickColor(color: string) {
    if (!this.editor) return;
    this.textColor = color;
    this.editor.chain().focus().setColor(color).run();
    this.showColorPicker = false;
  }

  /**
   * 设置高亮颜色
   *
   * @param color - 颜色值（空字符串表示取消高亮）
   */
  private pickHighlight(color: string) {
    if (!this.editor) return;
    this.highlightColor = color;
    if (color) {
      this.editor.chain().focus().setHighlight({ color }).run();
    } else {
      this.editor.chain().focus().unsetHighlight().run();
    }
    this.showHighlightPicker = false;
  }

  /**
   * 插入本地图片
   *
   * 动态创建 file input 并触发点击，选择图片后调用 processFiles 处理。
   */
  private insertLocalImage = () => {
    if (!this.imageFileInput) {
      this.imageFileInput = document.createElement('input');
      this.imageFileInput.type = 'file';
      this.imageFileInput.accept = 'image/*';
      this.imageFileInput.addEventListener('change', () => {
        const files = this.imageFileInput?.files;
        if (files && files.length > 0) this.processFiles(files);
        if (this.imageFileInput) this.imageFileInput.value = '';
      });
    }
    this.imageFileInput.click();
  }

  /**
   * 确认插入 URL 图片
   *
   * @param e - 表单提交事件
   */
  private confirmImage(e: Event) {
    e.preventDefault();
    if (!this.editor) return;
    const url = this.imageUrl.trim();
    if (url) this.editor.chain().focus().setImage({ src: url }).run();
    this.showImageModal = false;
  }

  /** 关闭图片模态框 */
  private closeImageModal = () => { this.showImageModal = false; }

  /**
   * 打开表格插入模态框
   *
   * 重置行列数为默认值 3×3。
   */
  private insertTable = () => {
    this.tableRows = 3;
    this.tableCols = 3;
    this.showTableModal = true;
  }

  /**
   * 确认插入表格
   *
   * @param e - 表单提交事件
   */
  private confirmTable(e: Event) {
    e.preventDefault();
    if (!this.editor) return;
    if (this.tableRows >= 1 && this.tableCols >= 1) {
      this.editor.chain().focus().insertTable({ rows: this.tableRows, cols: this.tableCols, withHeaderRow: true }).run();
    }
    this.showTableModal = false;
  }

  /** 关闭表格模态框 */
  private closeTableModal = () => { this.showTableModal = false; }

  /**
   * 打开文件选择器（附件上传）
   *
   * 动态创建 file input（支持多选），选择文件后调用 handleFilesSelected。
   */
  private openFilePicker = () => {
    if (!this.fileInput) {
      this.fileInput = document.createElement('input');
      this.fileInput.type = 'file';
      this.fileInput.multiple = true;
      this.fileInput.addEventListener('change', this.handleFilesSelected);
    }
    this.fileInput.click();
  }

  /**
   * 文件选择回调：处理选中的文件
   *
   * 图片文件作为内联图片插入编辑器，其他文件作为附件上传（派发事件）。
   */
  private handleFilesSelected = () => {
    const files = this.fileInput?.files;
    if (!files || files.length === 0) return;
    // 附件模式：inlineImages=false，图片不会被插入编辑器
    this.processFiles(files, false);
    if (this.fileInput) this.fileInput.value = '';
  }

  /**
   * 判断文件是否为图片
   *
   * @param file - 文件对象
   * @returns 是否为图片
   */
  private isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  /**
   * 插入内联图片文件
   *
   * 如果提供了 customImageUploader，使用自定义上传器；否则使用默认 API。
   * 上传成功后在编辑器光标位置插入图片。
   *
   * @param file - 图片文件
   * @returns Promise<void>
   */
  private async insertInlineImageFromFile(file: File) {
    try {
      if (this.customImageUploader) {
        const result = await this.customImageUploader(file);
        if (this.editor) {
          this.editor.chain().focus().setImage({ src: result.displayUrl }).run();
        }
        return;
      }
      // 默认上传路径：调用后端 API 上传内联图片
      const result = await uploadInlineImage(file, (_pct) => {});
      this.uploadedInlineImageIds.push({ id: result.data.id, cid: result.data.cid });
      if (this.editor) {
        // 图片请求通过 HttpOnly Cookie 鉴权，URL 中不携带敏感令牌
        this.editor.chain().focus().setImage({ src: `${API_BASE_URL}/emails/0/inline-images/${result.data.cid}` }).run();
      }
    } catch (err: any) {
      toast.error(err.message || '图片上传失败');
    }
  }

  /**
   * 拖拽悬停事件：显示拖放提示遮罩
   */
  private handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    this.isDragOver = true;
  }

  /**
   * 拖拽离开事件：隐藏拖放提示遮罩
   */
  private handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.isDragOver = false;
  }

  /**
   * 拖放事件：处理拖放的文件
   *
   * 图片文件作为内联图片插入，其他文件作为附件上传。
   */
  private handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.isDragOver = false;
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) this.processFiles(files);
  }

  /**
   * 处理文件列表
   *
   * 遍历文件，根据类型分别处理：
   * - 图片文件（且 inlineImages=true）：作为内联图片插入编辑器
   * - 其他文件：派发 crm-attachment-start 事件，上传为附件
   *
   * @param fileList - 文件列表
   * @param inlineImages - 是否将图片作为内联图片插入（默认 true）
   * @returns Promise<void>
   */
  private async processFiles(fileList: FileList, inlineImages = true) {
    for (const file of Array.from(fileList)) {
      if (inlineImages && this.isImageFile(file)) {
        await this.insertInlineImageFromFile(file);
      } else {
        this.dispatchEvent(new CustomEvent('crm-attachment-start', {
          detail: { name: file.name, size: file.size },
          bubbles: true, composed: true,
        }));
        try {
          const result = await uploadAttachment(file, (pct) => {
            this.dispatchEvent(new CustomEvent('crm-attachment-progress', {
              detail: { name: file.name, progress: pct },
              bubbles: true, composed: true,
            }));
          });
          this.uploadedAttachmentIds.push(result.data.id);
          this.dispatchEvent(new CustomEvent('crm-attachment-complete', {
            detail: { id: result.data.id, name: file.name, size: file.size },
            bubbles: true, composed: true,
          }));
        } catch (err: any) {
          this.dispatchEvent(new CustomEvent('crm-attachment-error', {
            detail: { name: file.name, error: err.message || '上传失败' },
            bubbles: true, composed: true,
          }));
          toast.error(err.message || '附件上传失败');
        }
      }
    }
  }

  /**
   * 导出已上传的附件 ID 列表
   *
   * 供父组件在发送邮件时获取附件 ID 数组。
   *
   * @returns 附件 ID 数组
   */
  exportAttachmentIds(): number[] {
    return this.uploadedAttachmentIds;
  }

  /**
   * 聚焦编辑器
   *
   * 使用 requestAnimationFrame 确保在 DOM 更新后聚焦。
   * 光标定位到内容开头。
   */
  focusEditor() {
    requestAnimationFrame(() => {
      this.editor?.commands.focus('start');
    });
  }

  /**
   * 在光标位置插入 HTML 内容
   *
   * 用于从外部插入片段、产品名等内容。
   *
   * @param html - 要插入的 HTML 字符串
   */
  insertAtCursor(html: string) {
    if (!this.editor) return;
    this.editor.chain().focus().insertContent(html).run();
  }

  /**
   * 导出已上传的内联图片 ID 列表
   *
   * @returns 内联图片 ID 数组
   */
  exportInlineImageIds(): number[] {
    return this.uploadedInlineImageIds.map(x => x.id);
  }

  /**
   * 重置附件和内联图片 ID 列表
   *
   * 在发送邮件或清除编辑器后调用，避免重复使用已发送的附件 ID。
   */
  resetAttachments() {
    this.uploadedAttachmentIds = [];
    this.uploadedInlineImageIds = [];
  }

  /**
   * 从编辑器中移除签名块
   *
   * 遍历 ProseMirror 文档树，查找所有 signatureBlock 类型节点并删除。
   * 通过节点类型名称查找，比 HTML 字符串匹配更可靠。
   */
  stripSignature() {
    if (!this.editor) return;
    const { state, view } = this.editor;
    const tr = state.tr;
    let found = false;
    state.doc.descendants((node, pos) => {
      if (node.type.name === 'signatureBlock') {
        tr.delete(pos, pos + node.nodeSize);
        found = true;
        return false;
      }
      return true;
    });
    if (found) view.dispatch(tr);
  }

  /**
   * 渲染富文本编辑器
   *
   * 布局结构：
   * 1. .editor-wrap：编辑器外层容器（支持拖放高亮）
   *    - .toolbar-wrapper > .toolbar：主工具栏（格式、颜色、字号、列表、对齐、缩进、链接、图片、表格、附件等按钮）
   *    - .toolbar-wrapper > .toolbar-extra-row：扩展工具栏行（slot="toolbar-extra"）
   *    - 拖放遮罩（条件渲染）
   *    - .editor-body：ProseMirror 编辑器挂载点
   * 2. .hidden-input：隐藏的 input（用于表单提交和 required 校验）
   * 3. 链接编辑模态框（条件渲染）
   * 4. 图片插入模态框（条件渲染）
   * 5. 表格插入模态框（条件渲染）
   *
   * 工具栏通过 slot 支持：
   * - slot="toolbar-end"：工具栏末尾追加（如签名选择器）
   * - slot="toolbar-extra"：第二行扩展工具栏（如片段/产品/模板下拉）
   *
   * @returns Lit 模板
   */
  render() {
    return html`
      <!-- 编辑器外层容器：支持拖放文件上传 -->
      <div class="editor-wrap ${this.isDragOver ? 'drag-over' : ''}"
           @dragover=${this.handleDragOver}
           @dragleave=${this.handleDragLeave}
           @drop=${this.handleDrop}>
        <div class="toolbar-wrapper">
        <!-- 主工具栏：格式按钮 + 颜色 + 字号 + 列表 + 对齐 + 缩进 + 链接 + 图片 + 表格 + 附件 + 撤销/重做 + 清除格式 -->
        <div class="toolbar">
          <button class="toolbar-btn ${this.isBold ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().toggleBold().run()} title="加粗">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" width="16" height="16"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/></svg>
          </button>
          <button class="toolbar-btn ${this.isItalic ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().toggleItalic().run()} title="斜体">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></svg>
          </button>
          <button class="toolbar-btn ${this.isStrike ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().toggleStrike().run()} title="删除线">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M16 4H9a3 3 0 0 0 0 6h6"/><path d="M13 14a3 3 0 0 1-4 6"/><line x1="4" y1="12" x2="20" y2="12"/></svg>
          </button>
          <button class="toolbar-btn ${this.isUnderline ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().toggleUnderline().run()} title="下划线">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M6 4v7a6 6 0 0 0 12 0V4"/><line x1="4" y1="20" x2="20" y2="20"/></svg>
          </button>
          <div class="toolbar-sep"></div>
          <div style="position:relative">
            <button class="toolbar-btn" @click=${() => { this.showColorPicker = !this.showColorPicker; this.showHighlightPicker = false; this.showFontSizePicker = false; }} title="字体颜色">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M4 20h16"/><path d="M9.354 5H14.646L18 17H6L9.354 5Z"/></svg>
              <span style="position:absolute;bottom:3px;left:50%;transform:translateX(-50%);width:12px;height:3px;background:${this.textColor};border-radius:1px;"></span>
            </button>
            ${this.showColorPicker ? html`
              <div class="bg-surface border border-border rounded-md shadow-xl p-2" style="position:absolute;z-index:9999;top:calc(100% + 4px);left:0;">
                <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:4px;">
                  ${['#1c2024','#eb8e90','#f97316','#eab308','#22c55e','#06b6d4','#3b82f6','#8b5cf6','#ec4899','#60646c','#ffffff','#ef4444','#f59e0b','#84cc16','#14b8a6','#0ea5e9','#6366f1','#a855f7','#f43f5e','#b0b4ba'].map(c => html`
                    <button @click=${() => this.pickColor(c)} style="width:24px;height:24px;border-radius:4px;cursor:pointer;border:1px solid var(--color-border,#e0e1e6);background:${c};" title=${c}></button>
                  `)}
                </div>
              </div>
            ` : ''}
          </div>
          <div style="position:relative">
            <button class="toolbar-btn" @click=${() => { this.showHighlightPicker = !this.showHighlightPicker; this.showColorPicker = false; this.showFontSizePicker = false; }} title="背景色">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M12 20h10"/><path d="M12 20V10"/><path d="M17 20V4"/><path d="M7 20h3"/><rect x="5" y="16" width="4" height="4" rx="1" fill="currentColor" opacity="0.3"/></svg>
              ${this.highlightColor ? html`<span style="position:absolute;bottom:3px;left:50%;transform:translateX(-50%);width:12px;height:3px;background:${this.highlightColor};border-radius:1px;"></span>` : ''}
            </button>
            ${this.showHighlightPicker ? html`
              <div class="bg-surface border border-border rounded-md shadow-xl p-2" style="position:absolute;z-index:9999;top:calc(100% + 4px);left:0;">
                <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:4px;">
                  ${['','#fef08a','#fde68a','#fed7aa','#bbf7d0','#bfdbfe','#ddd6fe','#fecdd3','#e2e8f0','#f1f5f9'].map(c => html`
                    <button @click=${() => this.pickHighlight(c)} style="width:24px;height:24px;border-radius:4px;cursor:pointer;border:1px solid var(--color-border,#e0e1e6);background:${c || 'transparent'};${!c ? 'background-image:linear-gradient(45deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%),linear-gradient(45deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%);background-size:6px 6px;background-position:0 0,3px 3px;' : ''}" title=${c || '无'}></button>
                  `)}
                </div>
              </div>
            ` : ''}
          </div>
          <div style="position:relative">
            <button class="toolbar-btn" @click=${() => { this.showFontSizePicker = !this.showFontSizePicker; this.showColorPicker = false; this.showHighlightPicker = false; }} title="字号" style="min-width:36px;font-size:12px;">
              <span>${this.fontSize.replace('px','')}</span>
            </button>
            ${this.showFontSizePicker ? html`
              <div class="bg-surface border border-border rounded-md shadow-xl overflow-hidden" style="position:absolute;z-index:9999;top:calc(100% + 4px);left:0;">
                <ul style="list-style:none;margin:0;padding:4px 0;">
                  ${['12px','14px','16px','18px','20px','24px','28px','32px','36px','48px'].map(s => html`
                    <li style="padding:6px 12px;font-size:13px;cursor:pointer;" class="hover:bg-bg" @click=${() => {
                      this.fontSize = s;
                      this.editor?.chain().focus().setFontSize(s).run();
                      this.showFontSizePicker = false;
                    }}>${s.replace('px','')}px</li>
                  `)}
                </ul>
              </div>
            ` : ''}
          </div>
          <div class="toolbar-sep"></div>
          <button class="toolbar-btn ${this.isH2 ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().toggleHeading({ level: 2 }).run()} title="标题">
            <span style="font-weight:600;font-size:14px;">H</span>
          </button>
          <button class="toolbar-btn ${this.isBulletList ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().toggleBulletList().run()} title="无序列表">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1" fill="currentColor"/><circle cx="3" cy="12" r="1" fill="currentColor"/><circle cx="3" cy="18" r="1" fill="currentColor"/></svg>
          </button>
          <button class="toolbar-btn ${this.isOrderedList ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().toggleOrderedList().run()} title="有序列表">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/></svg>
          </button>
          <button class="toolbar-btn ${this.isBlockquote ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().toggleBlockquote().run()} title="引用">
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621-.537 2.982.35 4.943 2.09 5.495.95.3 1.96.18 2.71-.36.68-.52.93-1.32.64-2.16-.23-.66-.82-1.14-1.58-1.21.24-.08.48-.08.72-.08 1.75 0 3.13 1.41 3.13 3.16 0 2.06-2.04 4.06-4.76 4.06-2.06 0-4.01-1-5.38-2.63zm10.8 0C13.35 16.227 12.8 15 12.8 13.011c0-3.5 2.46-6.637 6.03-8.188l.89 1.378c-3.33 1.804-3.99 4.145-4.25 5.621-.54 2.982.35 4.943 2.09 5.495.95.3 1.96.18 2.71-.36.68-.52.93-1.32.64-2.16-.23-.66-.82-1.14-1.58-1.21.24-.08.48-.08.72-.08 1.75 0 3.13 1.41 3.13 3.16 0 2.06-2.04 4.06-4.76 4.06-2.06 0-4.01-1-5.38-2.63z"/></svg>
          </button>
          <div class="toolbar-sep"></div>
          <button class="toolbar-btn ${this.textAlign === 'left' ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().setTextAlign('left').run()} title="左对齐">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>
          </button>
          <button class="toolbar-btn ${this.textAlign === 'center' ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().setTextAlign('center').run()} title="居中对齐">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
          </button>
          <button class="toolbar-btn ${this.textAlign === 'right' ? 'is-active' : ''}" @click=${() => this.editor?.chain().focus().setTextAlign('right').run()} title="右对齐">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>
          </button>
          <div class="toolbar-sep"></div>
          <button class="toolbar-btn" @click=${() => this.editor?.chain().focus().indent().run()} title="增加缩进">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/><path d="M3 12h3l-2 2 2 2H3"/></svg>
          </button>
          <button class="toolbar-btn" @click=${() => this.editor?.chain().focus().outdent().run()} title="减少缩进">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/><path d="M6 12H3l2 2-2 2h3"/></svg>
          </button>
          <div class="toolbar-sep"></div>
          <button class="toolbar-btn ${this.isLink ? 'is-active' : ''}" @click=${this.setLink} title="插入链接">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
          </button>
          <button class="toolbar-btn" @click=${this.insertLocalImage} title="插入图片">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
          </button>
          <button class="toolbar-btn" @click=${this.insertTable} title="插入表格">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><rect width="18" height="18" x="3" y="3" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
          </button>
          <button class="toolbar-btn" @click=${this.openFilePicker} title="上传附件">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
          </button>
          <button class="toolbar-btn" @click=${() => this.editor?.chain().focus().setHorizontalRule().run()} title="分割线">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="3" y1="12" x2="21" y2="12"/></svg>
          </button>
          <button class="toolbar-btn" @click=${() => this.editor?.chain().focus().undo().run()} title="撤销">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M3 10h10a5 5 0 0 1 0 10H9"/><path d="m3 10 4-4"/><path d="m3 10 4 4"/></svg>
          </button>
          <button class="toolbar-btn" @click=${() => this.editor?.chain().focus().redo().run()} title="重做">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M21 10H11a5 5 0 0 0 0 10h4"/><path d="m21 10-4-4"/><path d="m21 10-4 4"/></svg>
          </button>
          <button class="toolbar-btn" @click=${() => this.editor?.chain().focus().unsetAllMarks().clearNodes().run()} title="清除格式">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="m4 7 4-3 4 3"/><path d="M8 4v16"/><path d="m16 7 4-3 4 3"/><path d="M20 4v16"/><line x1="4" y1="20" x2="20" y2="20"/></svg>
          </button>
          <!-- 工具栏末尾插槽：用于注入签名选择器等外部组件 -->
          <slot name="toolbar-end"></slot>
        </div>
        <!-- 扩展工具栏行：用于注入片段/产品/模板下拉等外部组件 -->
        <div class="toolbar-extra-row">
          <slot name="toolbar-extra"></slot>
        </div>
        </div>
        <!-- 拖放提示遮罩：拖拽文件到编辑器时显示 -->
        ${this.isDragOver ? html`
          <div class="drag-overlay">
            <div class="drag-overlay-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#0d74ce" stroke-width="2" width="24" height="24"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
            </div>
            <span class="drag-overlay-text">释放以上传附件</span>
          </div>
        ` : ''}
        <!-- ProseMirror 编辑器挂载点 -->
        <div class="editor-body ${this.noScroll ? 'no-scroll' : ''}"></div>
      </div>
      <!-- 隐藏的 input：用于表单提交和 required 校验 -->
      <input class="hidden-input" type="text" name=${this.name} .value=${this.value} ?required=${this.required} tabindex="-1" />

      <!-- 链接编辑模态框 -->
      ${this.showLinkModal ? html`
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.closeLinkModal(); }}>
          <div class="bg-surface rounded-md shadow-xl w-full max-w-md border border-border mx-4" @click=${(e: Event) => e.stopPropagation()}>
            <div class="flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
              <h2 class="m-0 font-semibold text-lg text-text">插入链接</h2>
              <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.closeLinkModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <form @submit=${(e: Event) => this.confirmLink(e)} class="p-6">
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-text">链接地址</label>
                <input type="url" .value=${this.linkUrl} @input=${(e: Event) => { this.linkUrl = (e.target as HTMLInputElement).value; }} class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" placeholder="https://" autofocus />
              </div>
              <div class="flex justify-end gap-2">
                <button type="button" class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${this.closeLinkModal}>取消</button>
                ${this.linkUrl ? html`<button type="button" class="px-4 py-1.5 text-sm font-medium text-error hover:text-error-hover bg-bg hover:bg-error/5 border border-border rounded transition-colors cursor-pointer" @click=${() => { this.linkUrl = ''; this.confirmLink(new Event('submit', { bubbles: true }) as any); }}>移除链接</button>` : ''}
                <button type="submit" class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20">确定</button>
              </div>
            </form>
          </div>
        </div>
      ` : ''}

      <!-- 图片插入模态框 -->
      ${this.showImageModal ? html`
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.closeImageModal(); }}>
          <div class="bg-surface rounded-md shadow-xl w-full max-w-md border border-border mx-4" @click=${(e: Event) => e.stopPropagation()}>
            <div class="flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
              <h2 class="m-0 font-semibold text-lg text-text">插入图片</h2>
              <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.closeImageModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <form @submit=${(e: Event) => this.confirmImage(e)} class="p-6">
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-text">图片地址</label>
                <input type="url" .value=${this.imageUrl} @input=${(e: Event) => { this.imageUrl = (e.target as HTMLInputElement).value; }} class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" placeholder="https://" autofocus />
              </div>
              <div class="flex justify-end gap-2">
                <button type="button" class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${this.closeImageModal}>取消</button>
                <button type="submit" class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20">插入</button>
              </div>
            </form>
          </div>
        </div>
      ` : ''}

      <!-- 表格插入模态框 -->
      ${this.showTableModal ? html`
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.closeTableModal(); }}>
          <div class="bg-surface rounded-md shadow-xl w-full max-w-sm border border-border mx-4" @click=${(e: Event) => e.stopPropagation()}>
            <div class="flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
              <h2 class="m-0 font-semibold text-lg text-text">插入表格</h2>
              <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.closeTableModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <form @submit=${(e: Event) => this.confirmTable(e)} class="p-6">
              <div class="mb-4">
                <label class="block mb-1.5 text-sm font-medium text-text">行数</label>
                <input type="number" .value=${String(this.tableRows)} @input=${(e: Event) => { this.tableRows = parseInt((e.target as HTMLInputElement).value) || 1; }} min="1" max="50" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" autofocus />
              </div>
              <div class="mb-5">
                <label class="block mb-1.5 text-sm font-medium text-text">列数</label>
                <input type="number" .value=${String(this.tableCols)} @input=${(e: Event) => { this.tableCols = parseInt((e.target as HTMLInputElement).value) || 1; }} min="1" max="20" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" />
              </div>
              <div class="flex justify-end gap-2">
                <button type="button" class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${this.closeTableModal}>取消</button>
                <button type="submit" class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20">插入</button>
              </div>
            </form>
          </div>
        </div>
      ` : ''}
    `;
  }
}
